import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Users, MessageSquare, MessageCircle, TrendingUp, RefreshCw, Filter, Heart, Mic, CheckCircle2, X, AlertCircle } from "lucide-react";
import { computePriorityStats, PriorityFrequency, validatePriorityText, KEYWORD_TRANSLATIONS, usePrioritiesStore } from "@/data/prioritiesStore";
import { useAuth } from "@/contexts/AuthContext";
import AuthModal from "@/components/AuthModal";
import { useI18n } from "@/i18n/I18nProvider";
import { useCmsSection } from "@/hooks/useCmsSection";
import { useSettingsStore } from "@/data/settingsStore";

const TAG_COLORS = [
  "#6366f1", "#3b82f6", "#f59e0b", "#ec4899", "#10b981",
  "#8b5cf6", "#0ea5e9", "#f97316", "#84cc16", "#06b6d4",
];

type StatItem = { keyword: string; count: number; pct: number };

function PriorityTag({ keyword, count, pct, rank, color, textMentions }: StatItem & { rank: number; color: string; textMentions: string }) {
  const size = rank === 0 ? "text-2xl" : rank === 1 ? "text-xl" : rank === 2 ? "text-lg" : rank < 6 ? "text-base" : "text-sm";
  const pad = rank === 0 ? "px-5 py-3" : rank < 3 ? "px-4 py-2" : "px-3 py-1.5";
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: rank * 0.04, duration: 0.3 }}
      className={`inline-flex flex-col items-center ${pad} rounded-2xl border-2 cursor-default shadow-sm hover:shadow-md hover:scale-105 transition-all w-full sm:w-auto text-center`}
      style={{ borderColor: `${color}40`, background: `${color}10` }}
      title={`${count} ${textMentions} (${pct}%)`}
    >
      <span className={`font-display font-bold ${size}`} style={{ color }}>{keyword}</span>
      <span className="text-[9px] font-body opacity-60" style={{ color }}>{count} {textMentions}</span>
    </motion.div>
  );
}

export default function CitizenExpectations() {
  const { isAuthenticated, user } = useAuth();
  const { t, isRtl, lang } = useI18n();
  const e = t.expectations;
  const cms = useCmsSection("expectations");
  const { getSetting } = useSettingsStore();
  const hideCount = getSetting("kaynin_hide_expectations_count", "false") === "true";

  const FILTER_OPTIONS = [
    { id: "all",     label: e.filters.all,     emoji: "🌍", color: "#6366f1" },
    { id: "citizen", label: e.filters.citizen, emoji: "🧑", color: "#3b82f6" },
    { id: "young",   label: e.filters.young,   emoji: "⚡", color: "#f59e0b" },
    { id: "women",   label: e.filters.women,   emoji: "♀",  color: "#ec4899" },
    { id: "ong",     label: e.filters.ong,     emoji: "🤝", color: "#10b981" },
  ];

  const [activeFilter, setActiveFilter] = useState("");
  const [stats, setStats] = useState<StatItem[]>([]);
  const [totalResponses, setTotalResponses] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  
  const { priorities, submitPriority } = usePrioritiesStore();
  const allPriorities = priorities;
  const validatedPriorities = priorities.filter(p => p.status === "valide");

  // ── Modal state ──────────────────────────────────────────────────────────────
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [pendingOpenForm, setPendingOpenForm] = useState(false);
  const [formState, setFormState] = useState({ priority1: "", priority2: "", declaration: false });
  const [formErrors, setFormErrors] = useState<{ priority1?: string; priority2?: string; declaration?: string }>({});
  const [submitted, setSubmitted] = useState(false);
  const [rateLimitMessage, setRateLimitMessage] = useState(false);

  const rateLimitTexts = {
    title: { fr: "Merci pour votre contribution !", ar: "شكرا على مساهمتك!", zgh: "ⵜⴰⵏⵎⵎⵉⵔⵜ ⵏⵏⴽ!" },
    desc: { fr: "Vous avez déjà exprimé vos besoins prioritaires récemment. Vous pourrez en exprimer d'autres dans 12h.", ar: "لقد عبرت بالفعل عن احتياجاتك. يمكنك التعبير عن احتياجات أخرى خلال 12 ساعة.", zgh: "ⵜⵙⵙⵏⴽⴷⴷ ⵢⴰⴷ ⵜⵉⵔⵎⵉⵜⵉⵏ ⵏⵏⴽ. ⵜⵣⵎⵔⴷ ⴰⴷ ⵜⵙⵙⵏⴽⴷⴷ ⵜⵉⵢⵢⴰⴹ ⴳ 12 ⵜⵙⵔⴰⴳⵉⵏ." }
  };

  // Après connexion, ouvrir le formulaire si en attente
  useEffect(() => {
    if (isAuthenticated && pendingOpenForm) {
      setPendingOpenForm(false);
      setIsFormOpen(true);
    }
  }, [isAuthenticated, pendingOpenForm]);

  const handlePrendreLaParole = () => {
    if (isAuthenticated) {
      const userPriorities = allPriorities.filter(p => p.userId === user!.id);
      if (userPriorities.length > 0) {
        userPriorities.sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());
        const lastSubmitted = new Date(userPriorities[0].submittedAt).getTime();
        if (Date.now() - lastSubmitted < 12 * 60 * 60 * 1000) {
          setRateLimitMessage(true);
          return;
        }
      }

      setSubmitted(false);
      setFormState({ priority1: "", priority2: "", declaration: false });
      setFormErrors({});
      setIsFormOpen(true);
    } else {
      setPendingOpenForm(true);
      setIsAuthModalOpen(true);
    }
  };

  const computeStats = () => {
    let filter: Parameters<typeof computePriorityStats>[0] | undefined;
    if (activeFilter === "citizen") filter = { plus29Only: true };
    else if (activeFilter === "young") filter = { genZOnly: true };
    else if (activeFilter === "women") filter = { genre: "femme" };
    else if (activeFilter === "ong") filter = { role: "ONG" };
    
    const rawStats = computePriorityStats(filter);
    const totalMentions = rawStats.reduce((s, i) => s + i.count, 0);
    const mappedStats = rawStats.map(item => {
      let displayName = item.word;
      if (lang !== "fr" && KEYWORD_TRANSLATIONS[item.word.toLowerCase()]) {
        displayName = KEYWORD_TRANSLATIONS[item.word.toLowerCase()][lang] || item.word;
      } else {
        displayName = displayName.charAt(0).toUpperCase() + displayName.slice(1);
      }
      return {
        keyword: displayName,
        count: item.count,
        pct: totalMentions > 0 ? Math.round((item.count / totalMentions) * 100) : 0
      };
    });
    
    setStats(mappedStats);
    setTotalResponses(validatedPriorities.filter(p => {
      if (activeFilter === "citizen") return p.role === "CITIZEN" && p.ageGroup !== "18-24" && p.ageGroup !== "25-29";
      if (activeFilter === "young") return p.ageGroup === "18-24" || p.ageGroup === "25-29";
      if (activeFilter === "women") return p.genre === "femme";
      if (activeFilter === "ong") return p.role === "ONG";
      return true;
    }).length);
    setLastUpdate(new Date());
  };

  const handleFormSubmit = () => {
    const errors: typeof formErrors = {};
    const v1 = validatePriorityText(formState.priority1);
    const v2 = validatePriorityText(formState.priority2);
    if (!v1.valid) errors.priority1 = v1.error;
    if (!v2.valid) errors.priority2 = v2.error;
    if (!formState.declaration) errors.declaration = "Vous devez accepter la charte éthique";
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }

    submitPriority({
      userId: user!.id,
      userName: user!.name,
      role: (user!.role === "ONG" ? "ONG" : "CITIZEN") as "CITIZEN" | "ONG",
      genre: user?.genre,
      ageGroup: user?.age,
      ongName: user?.ongName,
      priority1: formState.priority1.trim(),
      priority2: formState.priority2.trim(),
    });
    setSubmitted(true);
    computeStats();
  };

  useEffect(() => { if (activeFilter) computeStats(); }, [activeFilter, lang, priorities]);

  const activeConfig = FILTER_OPTIONS.find(f => f.id === activeFilter) || FILTER_OPTIONS[0];
  const topStats = stats.slice(0, 10);
  const tagStats = stats.slice(0, 20);

  return (
    <section id="citizen-expectations-section" className="py-8 bg-gradient-to-b from-background via-primary/3 to-background" dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="container px-4 md:px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 font-body text-base md:text-lg font-extrabold mb-1.5 border border-red-200 dark:border-red-800 animate-pulse">
            <MessageCircle className="w-5 h-5" /> {e.badge}
          </span>
          <div className="flex items-center justify-center gap-4 mb-2">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-display font-extrabold text-foreground">
              {lang === 'ar' ? (cms.title_ar || e.title) : (cms.title_fr || e.title)}
            </h2>
          </div>
          <p className="text-sm md:text-base text-muted-foreground font-body max-w-2xl mx-auto">
            {lang === 'ar' ? (cms.subtitle_ar || e.subtitle) : (cms.subtitle_fr || e.subtitle)} {!hideCount && <strong>{totalResponses} {e.respondents}</strong>}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-4">
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              onClick={handlePrendreLaParole}
              className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 text-white font-body font-bold text-sm shadow-lg shadow-pink-500/30 hover:shadow-pink-500/50 transition-all ${isRtl ? 'flex-row-reverse' : ''}`}
            >
              <Mic className="w-4 h-4" />
              {e.btnSpeak}
            </motion.button>
            <button onClick={computeStats} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors">
              <RefreshCw className="w-3 h-3" /> {e.updatedAt} {lastUpdate.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
            </button>
          </div>
        </motion.div>

        <p className="text-center text-sm text-muted-foreground mb-3 italic">
          {lang === "ar" ? "اضغط للاطلاع على التفاصيل" : "Cliquez pour consulter"}
        </p>

        {/* Filter buttons */}
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {FILTER_OPTIONS.map(f => (
            <button
              key={f.id}
              onClick={() => setActiveFilter(activeFilter === f.id ? "" : f.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-body font-semibold transition-all duration-200 border ${
                activeFilter === f.id
                  ? "text-white shadow-md scale-105"
                  : "bg-card text-foreground border-border hover:border-primary/40"
              }`}
              style={activeFilter === f.id ? { background: f.color, borderColor: f.color } : {}}
            >
              <span>{f.emoji}</span>{f.label}
            </button>
          ))}
        </div>

        {activeFilter && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-6">
            {/* KPI banner */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: e.kpiRespondents, value: totalResponses, color: activeConfig.color },
            { label: e.kpiUnique, value: stats.length, color: "#8b5cf6" },
            { label: e.kpiTop, value: stats[0]?.keyword || "—", color: "#f59e0b" },
            { label: e.kpiTop3, value: stats.slice(0,3).reduce((s,v)=>s+v.pct,0) + "%", color: "#10b981" },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-card border border-border rounded-2xl p-4 text-center shadow-sm">
              <p className="text-xl md:text-2xl font-display font-black" style={{ color }}>{value}</p>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mt-0.5">{label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Word Cloud */}
          <div className="lg:col-span-3 bg-card border border-border rounded-2xl p-6 shadow-sm">
            <p className="text-sm font-display font-bold text-foreground mb-5 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" style={{ color: activeConfig.color }} />
              {e.cloudTitle} <span style={{ color: activeConfig.color }}>{activeConfig.emoji} {activeConfig.label}</span>
            </p>
            {tagStats.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
                <p className="text-sm text-muted-foreground">{e.emptyData}</p>
              </div>
            ) : (
              <div className="flex flex-wrap gap-3 justify-center items-center min-h-[200px]">
                {tagStats.map((s, i) => (
                  <PriorityTag key={s.keyword} {...s} rank={i} color={TAG_COLORS[i % TAG_COLORS.length]} textMentions={e.mentions} />
                ))}
              </div>
            )}
            <p className="text-[10px] text-muted-foreground text-center mt-4 italic">
              {e.cloudNote}
            </p>
          </div>

          {/* Bar chart */}
          <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-6 shadow-sm flex flex-col">
            <p className="text-sm font-display font-bold text-foreground mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" style={{ color: activeConfig.color }} />{e.barTitle}
            </p>
            {topStats.length === 0 ? (
              <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">{e.emptyData}</div>
            ) : (
              <div className="flex-1 min-h-[280px]">
                <ResponsiveContainer width="100%" height="100%" className={isRtl ? 'scale-x-[-1]' : ''}>
                  <BarChart data={topStats} layout="vertical" margin={{ top: 0, right: 30, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
                    <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                    <YAxis dataKey="keyword" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 600 }} width={isRtl ? 0 : 110} hide={isRtl} />
                    <Tooltip
                      formatter={(v: number, _: string, props: { payload?: { pct: number } }) => [`${v} ${e.mentions} (${props.payload?.pct}%)`, e.frequency]}
                      contentStyle={{ borderRadius: 10, border: "none", fontSize: 11, direction: "ltr" }}
                    />
                    <Bar dataKey="count" radius={isRtl ? [6, 0, 0, 6] : [0, 6, 6, 0]} maxBarSize={18}>
                      {topStats.map((_, i) => <Cell key={i} fill={TAG_COLORS[i % TAG_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* Full ranked list */}
        {stats.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 bg-card border border-border rounded-2xl shadow-sm overflow-hidden"
          >
            <div className={`px-4 py-3 border-b border-border bg-muted/20 flex items-center gap-2`}>
              <Filter className="w-4 h-4 text-muted-foreground" />
              <p className="text-sm font-display font-bold text-foreground">{e.tableTitle}</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" dir={isRtl ? 'rtl' : 'ltr'}>
                <thead className="bg-muted/30 text-muted-foreground font-semibold uppercase">
                  <tr>
                    <th className={`px-4 py-2 ${isRtl ? 'text-right' : 'text-left'} w-10`}>{e.colRank}</th>
                    <th className={`px-4 py-2 ${isRtl ? 'text-right' : 'text-left'}`}>{e.colPriority}</th>
                    <th className={`px-4 py-2 ${isRtl ? 'text-left' : 'text-right'}`}>{e.colMentions}</th>
                    <th className={`px-4 py-2 ${isRtl ? 'text-left' : 'text-right'}`}>{e.colPct}</th>
                    <th className={`px-4 py-2 ${isRtl ? 'text-right' : 'text-left'} w-40`}>{e.colPopularity}</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.slice(0, 15).map(({ keyword, count, pct }, i) => {
                    const color = TAG_COLORS[i % TAG_COLORS.length];
                    return (
                      <tr key={keyword} className="border-t border-border/50 hover:bg-muted/10 transition-colors">
                        <td className="px-4 py-2.5">
                          <span className="w-6 h-6 rounded-full inline-flex items-center justify-center text-[10px] font-black text-white" style={{ background: color }}>{i + 1}</span>
                        </td>
                        <td className="px-4 py-2.5 font-semibold text-foreground">{keyword}</td>
                        <td className={`px-4 py-2.5 font-mono font-bold ${isRtl ? 'text-left' : 'text-right'}`} style={{ color }}>{count}</td>
                        <td className={`px-4 py-2.5 ${isRtl ? 'text-left' : 'text-right'}`}>
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-muted">{pct}%</span>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className={`w-32 h-2 bg-muted/60 rounded-full overflow-hidden ${isRtl ? 'rotate-180' : ''}`}>
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-center text-muted-foreground py-3 border-t border-border">
              {e.tableNote}
            </p>
          </motion.div>
          )}

          <div className="flex justify-center mt-6 pb-2">
            <button 
              onClick={() => {
                setActiveFilter("");
                document.getElementById("citizen-expectations-section")?.scrollIntoView({ behavior: "smooth" });
              }} 
              className={`px-8 py-3 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-2xl font-bold text-sm shadow-sm transition-all border border-border flex items-center gap-2`}
            >
              {e.btnLess}
            </button>
          </div>
        </motion.div>
        )}
      </div>

      {/* ── Auth Modal ── */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => { setIsAuthModalOpen(false); setPendingOpenForm(false); }}
      />

      {/* ── Formulaire "Prendre la parole" ── */}
      <AnimatePresence>
        {isFormOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={(evt) => { if (evt.target === evt.currentTarget) setIsFormOpen(false); }}
            dir={isRtl ? 'rtl' : 'ltr'}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-card border border-border rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              {/* Header */}
              <div className="relative bg-gradient-to-r from-pink-500 to-rose-500 px-6 py-5">
                <button
                  onClick={() => setIsFormOpen(false)}
                  className={`absolute top-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors ${isRtl ? 'left-4' : 'right-4'}`}
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <Mic className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-display font-bold text-lg leading-tight">{e.modalTitle}</h3>
                    <p className="text-white/80 text-xs font-body">{e.modalSubtitle}</p>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="p-6">
                {!submitted ? (
                  <div className="space-y-4">
                    {/* Priority 1 */}
                    <div>
                      <label className="block text-sm font-body font-semibold text-foreground mb-1.5 flex items-center gap-1">
                        {e.priority1} <span className="text-rose-500">*</span>
                        <span className="font-normal text-muted-foreground">{e.maxWords}</span>
                      </label>
                      <input
                        type="text"
                        value={formState.priority1}
                        onChange={(evt) => { setFormState(s => ({ ...s, priority1: evt.target.value })); setFormErrors(err => ({ ...err, priority1: undefined })); }}
                        placeholder={e.placeholder1}
                        maxLength={40}
                        className={`w-full h-10 rounded-xl border ${
                          formErrors.priority1 ? "border-destructive" : "border-input"
                        } bg-background px-3 py-2 text-sm font-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-400`}
                      />
                      {formErrors.priority1 && (
                        <p className="text-xs text-destructive font-body mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />{formErrors.priority1}
                        </p>
                      )}
                    </div>

                    {/* Priority 2 */}
                    <div>
                      <label className="block text-sm font-body font-semibold text-foreground mb-1.5 flex items-center gap-1">
                        {e.priority2} <span className="text-rose-500">*</span>
                        <span className="font-normal text-muted-foreground">{e.maxWords}</span>
                      </label>
                      <input
                        type="text"
                        value={formState.priority2}
                        onChange={(evt) => { setFormState(s => ({ ...s, priority2: evt.target.value })); setFormErrors(err => ({ ...err, priority2: undefined })); }}
                        placeholder={e.placeholder2}
                        maxLength={40}
                        className={`w-full h-10 rounded-xl border ${
                          formErrors.priority2 ? "border-destructive" : "border-input"
                        } bg-background px-3 py-2 text-sm font-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-400`}
                      />
                      {formErrors.priority2 && (
                        <p className="text-xs text-destructive font-body mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />{formErrors.priority2}
                        </p>
                      )}
                    </div>

                    {/* Info publique */}
                    <div className="bg-muted/40 rounded-xl px-4 py-3 text-xs text-muted-foreground font-body">
                      {e.reminder}
                    </div>

                    {/* Déclaration éthique */}
                    <div className={`flex items-start gap-3 p-3 rounded-xl border ${
                      formErrors.declaration ? "border-destructive bg-destructive/5" : "border-border"
                    }`}>
                      <input
                        type="checkbox"
                        id="ethics-check"
                        checked={formState.declaration}
                        onChange={(evt) => { setFormState(s => ({ ...s, declaration: evt.target.checked })); setFormErrors(err => ({ ...err, declaration: undefined })); }}
                        className="mt-0.5 accent-pink-500 w-4 h-4 shrink-0"
                      />
                      <label htmlFor="ethics-check" className="text-xs font-body text-muted-foreground leading-relaxed cursor-pointer">
                        {e.ethicsText}
                      </label>
                    </div>
                    {formErrors.declaration && (
                      <p className="text-xs text-destructive font-body flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />{formErrors.declaration}
                      </p>
                    )}

                    {/* Submit */}
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleFormSubmit}
                      className="w-full py-3 rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 text-white font-body font-bold text-sm shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
                    >
                      <Mic className="w-4 h-4" />
                      {e.btnSubmit}
                    </motion.button>
                  </div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-8"
                  >
                    <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                    </div>
                    <h4 className="font-display font-bold text-lg text-foreground mb-2">{e.successTitle}</h4>
                    <p className="text-sm text-muted-foreground font-body mb-6 max-w-xs mx-auto">
                      {e.successDesc}
                    </p>
                    <button
                      onClick={() => setIsFormOpen(false)}
                      className="px-6 py-2.5 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 text-white font-body font-bold text-sm"
                    >
                      {e.btnClose}
                    </button>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Message de Rate Limit (12h) ── */}
      <AnimatePresence>
        {rateLimitMessage && (
          <div 
            className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-6 bg-transparent"
            onClick={() => setRateLimitMessage(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="bg-background border-2 border-emerald-500 rounded-2xl shadow-2xl p-4 max-w-sm w-full cursor-pointer"
              onClick={() => setRateLimitMessage(false)}
              dir={isRtl ? 'rtl' : 'ltr'}
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                  <Heart className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="font-bold text-sm text-foreground">{lang === 'ar' ? rateLimitTexts.title.ar : lang === 'zgh' ? rateLimitTexts.title.zgh : rateLimitTexts.title.fr}</p>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {lang === 'ar' ? rateLimitTexts.desc.ar : lang === 'zgh' ? rateLimitTexts.desc.zgh : rateLimitTexts.desc.fr}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
}
